/**
 * @fileoverview Shared Arrow cell conversion used by both the eager row
 * materialiser (`workers/duckdb.ts`) and the lazy `RiftData` accessor
 * (`stores/dataset-rifts/rift-data.ts`).
 *
 * DuckDB widens integer aggregates (SUM/COUNT over INTEGER/BIGINT) to HUGEINT,
 * which Arrow encodes as `Decimal128`. Reading such a column via
 * `Vector.get()` yields a `DecimalBigNum` object, and via `Vector.toArray()`
 * yields a flat `Uint32Array` of 128-bit words — neither is a usable JS number,
 * so charts (AG Charts drops non-numbers) and formatters silently break. These
 * helpers convert Decimal cells to scale-aware `number`s and normalise `NaN`.
 */

import type { DataType as ArrowDataTypeClass } from "apache-arrow";

/**
 * Arrow `Type.Decimal` enum value. Hard-coded (rather than importing the arrow
 * runtime here) because it is stable across the app's apache-arrow v21 and the
 * v17 bundled by duckdb-wasm that actually constructs the result tables.
 */
const ARROW_TYPE_DECIMAL = 7;

/** Normalises a raw Arrow cell value: genuine `NaN` becomes `null`. */
export const normalizeCell = (value: unknown): unknown =>
  typeof value === "number" && Number.isNaN(value) ? null : value;

/** True when an Arrow field type is Decimal (DuckDB HUGEINT / DECIMAL). */
export const isDecimalType = (type: ArrowDataTypeClass): boolean =>
  type.typeId === ARROW_TYPE_DECIMAL;

/**
 * Builds a cell converter for a single Arrow column from its field type.
 * Decimal columns are converted to scale-aware JS numbers (`DecimalBigNum`
 * stringifies to its unscaled integer digits); all other columns pass through
 * {@link normalizeCell}.
 */
export const buildCellConverter = (
  type: ArrowDataTypeClass,
): ((value: unknown) => unknown) => {
  if (isDecimalType(type)) {
    const scale = (type as { scale?: number }).scale ?? 0;
    const divisor = 10 ** scale;
    return (value: unknown) => {
      if (value == null) return null;
      const n = Number(value.toString());
      if (!Number.isFinite(n)) return null;
      return divisor === 1 ? n : n / divisor;
    };
  }
  return normalizeCell;
};
